// src/pages/Business.jsx
import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  Fade,
  Grow,
  Zoom,
  useMediaQuery,
  useTheme,
  alpha,
  Chip
} from '@mui/material';
import {
  Business as BusinessIcon,
  Storefront,
  Diamond,
  TrendingUp,
  Phone,
  Email,
  LocationOn,
  CheckCircle,
  Security,
  SupportAgent,
  ArrowForward,
  Analytics,
  Speed,
  AccountBalance,
  WorkspacePremium,
  Verified,
  FlashOn,
  Stars
} from '@mui/icons-material';
import { motion } from "framer-motion";

// Professional DiamondBank Zimbabwe color palette - Dark Gray/Black with subtle purple accent
const colors = {
  primary: '#1a1a1a',
  primaryLight: '#2d2d2d',
  primaryDark: '#0d0d0d',
  accent: '#6b21a5',
  accentLight: '#8b5cc9',
  accentDark: '#4c1d6e',
  secondary: '#e5e7eb',
  white: '#ffffff',
  black: '#000000',
  gray: '#6b7280',
  grayLight: '#9ca3af',
  grayDark: '#374151',
  lightBg: '#f8f9fa',
  textDark: '#1a1a1a',
  textLight: '#ffffff',
  gold: '#d4af37'
};

// Use image from public folder
const businessHero = '/images/hero.png';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
};

const Business = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const accountTypes = [
    {
      icon: <Storefront sx={{ fontSize: 40, color: colors.white }} />,
      title: "Business Starter",
      description: "Perfect for small businesses and startups in Zimbabwe",
      features: [
        "No minimum balance requirement",
        "Activation fee of ZWL 20,000",
        "Monthly Maintenance fee of ZWL 8,000",
        "10 free transactions per month",
        "Mobile banking included"
      ],
      action: "View Details",
      gradient: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
      badgeColor: colors.accent
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: colors.white }} />,
      title: "Commercial Growth",
      description: "For growing businesses with increasing needs",
      features: [
        "Annual revenues ZWL 500M+",
        "Activation fee of ZWL 50,000",
        "Monthly Maintenance fee of ZWL 12,000",
        "25 free transactions per month",
        "Dedicated relationship manager"
      ],
      action: "View Details",
      gradient: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
      badgeColor: colors.gold
    },
    {
      icon: <Diamond sx={{ fontSize: 40, color: colors.white }} />,
      title: "Enterprise Elite",
      description: "Premium banking for large corporations",
      features: [
        "Annual revenues ZWL 5B+",
        "Activation fee of ZWL 150,000",
        "Monthly Maintenance fee of ZWL 20,000",
        "Unlimited transactions",
        "Priority global support"
      ],
      action: "View Details",
      gradient: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.gray} 100%)`,
      badgeColor: colors.accentLight
    }
  ];

  const features = [
    {
      icon: <BusinessIcon sx={{ fontSize: 32, color: colors.accent }} />,
      title: "Business Expertise",
      description: "Bank with professionals who understand the Zimbabwean business landscape and your industry challenges"
    },
    {
      icon: <Security sx={{ fontSize: 32, color: colors.accent }} />,
      title: "Advanced Security",
      description: "Enterprise-grade protection with multi-factor authentication and fraud monitoring"
    },
    {
      icon: <SupportAgent sx={{ fontSize: 32, color: colors.accent }} />,
      title: "Dedicated Support",
      description: "24/7 priority assistance from experienced business banking specialists in Zimbabwe"
    },
    {
      icon: <Analytics sx={{ fontSize: 32, color: colors.accent }} />,
      title: "Business Intelligence",
      description: "Advanced analytics and reporting tools to track your financial performance"
    },
    {
      icon: <Speed sx={{ fontSize: 32, color: colors.accent }} />,
      title: "Fast Transactions",
      description: "Real-time payments and instant fund transfers for business efficiency in Zimbabwe"
    }
  ];

  const benefits = [
    "Competitive interest rates on business deposits",
    "Flexible overdraft facilities up to ZWL 50,000,000",
    "Business credit cards with rewards program",
    "Merchant services and payment processing",
    "International trade finance solutions",
    "Payroll and bulk payment processing"
  ];

  const stats = [
    { value: "98%", label: "Client Satisfaction", icon: <Verified /> },
    { value: "24/7", label: "Business Support", icon: <SupportAgent /> },
    { value: "ZWL 50M+", label: "Credit Facilities", icon: <AccountBalance /> },
    { value: "1000+", label: "Business Clients", icon: <BusinessIcon /> }
  ];

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <Box sx={{ overflowX: 'hidden', backgroundColor: colors.lightBg }}>
        {/* Hero Section - Improved with better glass effect */}
        <Box
          sx={{
            height: '75vh',
            minHeight: '600px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundImage: `linear-gradient(135deg, ${colors.primary}dd 0%, ${colors.accent}99 100%), url(${businessHero})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            color: 'white',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '120px',
              background: `linear-gradient(to top, ${colors.lightBg}, transparent)`,
              zIndex: 1
            }
          }}
        >
          {/* Animated background pattern */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.08,
            backgroundImage: `radial-gradient(circle at 20% 50%, ${colors.white} 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
            zIndex: 1
          }} />

          <Container sx={{ position: 'relative', zIndex: 2 }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Box sx={{ mb: 3 }}>
                <Chip
                  icon={<Stars sx={{ fontSize: 16 }} />}
                  label="PREMIUM BUSINESS BANKING - ZIMBABWE"
                  sx={{
                    backgroundColor: alpha(colors.white, 0.2),
                    color: colors.white,
                    fontWeight: 600,
                    letterSpacing: 1,
                    fontSize: '0.75rem',
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${alpha(colors.accent, 0.5)}`,
                    mb: 3
                  }}
                />
              </Box>
              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  fontSize: isMobile ? '2.5rem' : '4rem',
                  textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                  mb: 3,
                  lineHeight: 1.2
                }}
              >
                <Box component="span" sx={{ 
                  background: `linear-gradient(135deg, ${colors.white}, ${colors.accentLight}, ${colors.gold})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Business
                </Box> Banking Solutions
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  maxWidth: '800px',
                  mx: 'auto',
                  mb: 4,
                  textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  fontSize: isMobile ? '1rem' : '1.3rem',
                  fontWeight: 400,
                  opacity: 0.95,
                  color: alpha(colors.white, 0.95)
                }}
              >
                Tailored financial solutions to help your business grow, scale, and thrive in Zimbabwe's competitive market
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  variant="contained"
                  size="large"
                  href="/apply"
                  sx={{
                    px: 5,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: '50px',
                    background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`,
                    color: colors.white,
                    boxShadow: `0 8px 32px ${alpha(colors.accent, 0.4)}`,
                    '&:hover': {
                      boxShadow: `0 12px 40px ${alpha(colors.accent, 0.5)}`,
                      transform: 'translateY(-2px)',
                      background: colors.accentLight
                    },
                    transition: 'all 0.3s ease',
                    fontWeight: 700
                  }}
                >
                  Open Business Account
                </Button>
                <Button
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  variant="outlined"
                  size="large"
                  href="/contact"
                  sx={{
                    px: 5,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: '50px',
                    borderWidth: '2px',
                    borderColor: colors.white,
                    color: colors.white,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: alpha(colors.white, 0.1),
                      borderWidth: '2px',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Contact Business Advisor
                </Button>
              </Box>
            </motion.div>
          </Container>
        </Box>

        <Container sx={{ py: 8, maxWidth: '1400px' }}>
          {/* Business Banking Section - Improved */}
          <Grid container spacing={6} sx={{ mb: 10 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Box>
                  <Chip
                    label="BUSINESS BANKING - ZIMBABWE"
                    sx={{
                      backgroundColor: alpha(colors.accent, 0.1),
                      color: colors.accent,
                      fontWeight: 700,
                      letterSpacing: 1,
                      mb: 2,
                      fontSize: '0.75rem',
                      borderRadius: '8px'
                    }}
                  />
                  <Typography
                    variant="h2"
                    gutterBottom
                    sx={{
                      fontWeight: 800,
                      color: colors.primary,
                      mb: 2,
                      fontSize: isMobile ? '2rem' : '2.8rem',
                      position: 'relative'
                    }}
                  >
                    Business Banking
                  </Typography>
                  <Typography variant="h5" sx={{ color: colors.gray, fontWeight: 400, mb: 3, fontSize: isMobile ? '1.1rem' : '1.3rem' }}>
                    Comprehensive financial solutions for your enterprise in Zimbabwe
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ fontSize: '1rem', lineHeight: 1.8, color: colors.gray, mb: 3 }}>
                    Manage your business finances efficiently with DiamondBank Zimbabwe's specialized banking services.
                    From cash management to payroll solutions, we provide the tools your business needs to succeed in the local market.
                  </Typography>
                  <List dense sx={{ mb: 4 }}>
                    {[
                      "Dedicated business relationship managers",
                      "Bulk payment processing",
                      "Customized reporting tools",
                      "Multi-user access controls",
                      "Integrated accounting software"
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.75 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircle sx={{ color: colors.accent, fontSize: 20 }} />
                        </ListItemIcon>
                        <Typography variant="body1" sx={{ color: colors.textDark, fontWeight: 500 }}>{item}</Typography>
                      </ListItem>
                    ))}
                  </List>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      variant="contained"
                      size="large"
                      href="/apply"
                      sx={{
                        px: 4,
                        py: 1.25,
                        fontSize: '0.95rem',
                        borderRadius: '50px',
                        background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`,
                        fontWeight: 700,
                        boxShadow: `0 4px 15px ${alpha(colors.accent, 0.3)}`,
                        '&:hover': {
                          background: colors.accentLight,
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      Open Business Account
                    </Button>
                    <Button
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      variant="outlined"
                      size="large"
                      href="/features"
                      sx={{
                        px: 4,
                        py: 1.25,
                        fontSize: '0.95rem',
                        borderRadius: '50px',
                        borderWidth: '2px',
                        borderColor: colors.accent,
                        color: colors.accent,
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: alpha(colors.accent, 0.05),
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      View Business Features
                    </Button>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Box sx={{
                  p: 4,
                  borderRadius: '24px',
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                  color: 'white',
                  boxShadow: `0 20px 40px ${alpha(colors.primary, 0.3)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 150,
                    height: 150,
                    borderRadius: '50%',
                    background: alpha(colors.accent, 0.1),
                    zIndex: 0
                  }
                }}>
                  <Typography variant="h5" sx={{ color: colors.accent, fontWeight: 700, mb: 3, position: 'relative', zIndex: 1 }}>
                    Why Choose DiamondBank Zimbabwe?
                  </Typography>
                  <List sx={{ position: 'relative', zIndex: 1 }}>
                    {benefits.map((benefit, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 1.25 }}>
                        <ListItemIcon>
                          <CheckCircle sx={{ color: colors.accent }} />
                        </ListItemIcon>
                        <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>{benefit}</Typography>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </motion.div>
            </Grid>
          </Grid>

          {/* Account Types Section - Improved Cards */}
          <Box sx={{ mb: 10 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <motion.div variants={itemVariants} viewport={{ once: true }}>
                <Chip
                  label="BUSINESS SOLUTIONS - ZIMBABWE"
                  sx={{
                    backgroundColor: alpha(colors.accent, 0.1),
                    color: colors.accent,
                    fontWeight: 700,
                    letterSpacing: 1,
                    mb: 2,
                    fontSize: '0.85rem',
                    borderRadius: '8px'
                  }}
                />
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    color: colors.primary,
                    position: 'relative',
                    display: 'inline-block',
                    fontSize: isMobile ? '2rem' : '2.8rem',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '80px',
                      height: '3px',
                      background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                      borderRadius: '2px'
                    }
                  }}
                >
                  Business Accounts
                </Typography>
                <Typography variant="h5" sx={{ mt: 3, color: colors.gray, fontWeight: 400, fontSize: isMobile ? '1rem' : '1.2rem' }}>
                  Choose the right account for your business needs in Zimbabwe
                </Typography>
              </motion.div>
            </Box>
            <Grid container spacing={4} justifyContent="center">
              {accountTypes.map((account, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    whileHover={{ y: -8 }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '24px',
                        overflow: 'hidden',
                        background: 'white',
                        boxShadow: `0 8px 28px ${alpha(colors.primary, 0.08)}`,
                        border: `1px solid ${alpha(colors.gray, 0.1)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 16px 40px ${alpha(colors.accent, 0.15)}`,
                          borderColor: alpha(colors.accent, 0.3),
                        }
                      }}
                    >
                      <Box
                        sx={{
                          height: 140,
                          background: account.gradient,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          flexDirection: 'column',
                          gap: 2
                        }}
                      >
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            backgroundColor: alpha(colors.white, 0.2),
                            backdropFilter: 'blur(8px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                          }}
                        >
                          {account.icon}
                        </Box>
                        <Chip
                          label={account.title === "Enterprise Elite" ? "PREMIUM" : account.title === "Commercial Growth" ? "POPULAR" : "STANDARD"}
                          size="small"
                          sx={{
                            backgroundColor: account.badgeColor,
                            color: colors.white,
                            fontWeight: 700,
                            fontSize: '0.7rem'
                          }}
                        />
                      </Box>
                      <Box sx={{ p: 3, flexGrow: 1 }}>
                        <Typography variant="h5" gutterBottom sx={{
                          color: colors.primary,
                          fontWeight: 800,
                          textAlign: 'center',
                          fontSize: '1.4rem'
                        }}>
                          {account.title}
                        </Typography>
                        <Typography variant="body2" paragraph sx={{
                          color: colors.gray,
                          textAlign: 'center',
                          mb: 3,
                          fontSize: '0.85rem'
                        }}>
                          {account.description}
                        </Typography>
                        <List dense sx={{ mb: 2 }}>
                          {account.features.map((feature, i) => (
                            <ListItem key={i} sx={{ px: 0, py: 0.75 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckCircle sx={{ color: colors.accent, fontSize: 16 }} />
                              </ListItemIcon>
                              <Typography variant="body2" sx={{ color: colors.textDark, fontSize: '0.85rem', fontWeight: 500 }}>
                                {feature}
                              </Typography>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                      <Box sx={{ p: 3, pt: 0 }}>
                        <Button
                          component={motion.button}
                          whileHover={{ scale: 1.02 }}
                          variant="contained"
                          fullWidth
                          href={`/business/${account.title.toLowerCase().replace(/\s+/g, '-')}`}
                          sx={{
                            borderRadius: '50px',
                            py: 1.2,
                            background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`,
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            textTransform: 'none',
                            '&:hover': {
                              background: colors.accentLight,
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          {account.action}
                        </Button>
                      </Box>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Stats Section - Improved */}
          <Box sx={{
            mb: 10,
            py: 6,
            px: 3,
            borderRadius: '28px',
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `radial-gradient(circle at 20% 50%, ${colors.accent} 1px, transparent 1px)`,
              backgroundSize: '40px 40px',
              opacity: 0.1
            }
          }}>
            <Grid container spacing={4} justifyContent="center" textAlign="center">
              {stats.map((stat, index) => (
                <Grid item xs={6} md={3} key={index}>
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, type: "spring", stiffness: 200 }}
                  >
                    <Box sx={{ color: colors.accent, mb: 1, fontSize: 40 }}>{stat.icon}</Box>
                    <Typography variant="h2" sx={{ fontWeight: 800, color: colors.accent, mb: 1, fontSize: isMobile ? '2rem' : '2.5rem' }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.85, fontWeight: 500 }}>
                      {stat.label}
                    </Typography>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Features Section - Improved */}
          <Box sx={{ mb: 10 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <motion.div variants={itemVariants} viewport={{ once: true }}>
                <Chip
                  label="BUSINESS BENEFITS - ZIMBABWE"
                  sx={{
                    backgroundColor: alpha(colors.accent, 0.1),
                    color: colors.accent,
                    fontWeight: 700,
                    letterSpacing: 1,
                    mb: 2,
                    fontSize: '0.85rem',
                    borderRadius: '8px'
                  }}
                />
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    color: colors.primary,
                    position: 'relative',
                    display: 'inline-block',
                    fontSize: isMobile ? '2rem' : '2.8rem',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '80px',
                      height: '3px',
                      background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                      borderRadius: '2px'
                    }
                  }}
                >
                  Why Choose DiamondBank Zimbabwe?
                </Typography>
              </motion.div>
            </Box>
            <Grid container spacing={3} justifyContent="center">
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    whileHover={{ y: -5 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 4,
                        borderRadius: '20px',
                        background: 'white',
                        boxShadow: `0 8px 24px ${alpha(colors.primary, 0.05)}`,
                        border: `1px solid ${alpha(colors.gray, 0.1)}`,
                        transition: 'all 0.3s ease',
                        height: '100%',
                        textAlign: 'center',
                        '&:hover': {
                          boxShadow: `0 12px 32px ${alpha(colors.accent, 0.12)}`,
                          borderColor: alpha(colors.accent, 0.2),
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 70,
                          height: 70,
                          borderRadius: '50%',
                          backgroundColor: alpha(colors.accent, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          mx: 'auto',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: alpha(colors.accent, 0.2),
                            transform: 'scale(1.05)'
                          }
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" gutterBottom sx={{ color: colors.primary, fontWeight: 800, mb: 1.5 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.gray, lineHeight: 1.7 }}>
                        {feature.description}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* CTA Section - Improved */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
              color: 'white',
              borderRadius: '28px',
              p: { xs: 4, md: 6 },
              mb: 6,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage: `radial-gradient(circle at 20% 50%, ${colors.white} 1px, transparent 1px)`,
                backgroundSize: '40px 40px',
                opacity: 0.1
              }
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              style={{ position: 'relative', zIndex: 1 }}
            >
              <Diamond sx={{ fontSize: 60, color: colors.white, mb: 2, animation: 'spin 10s linear infinite' }} />
              <Typography variant="h2" gutterBottom sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                Grow Your Business With Us in Zimbabwe
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.95, fontWeight: 400, fontSize: { xs: '1rem', md: '1.2rem' } }}>
                Let's discuss how DiamondBank Zimbabwe can support your business goals
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  variant="contained"
                  size="large"
                  href="/apply"
                  sx={{
                    px: 5,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: '50px',
                    background: colors.white,
                    color: colors.accent,
                    fontWeight: 800,
                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                    '&:hover': {
                      background: colors.secondary,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
                    }
                  }}
                >
                  Apply Now
                </Button>
                <Button
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  variant="outlined"
                  size="large"
                  href="/contact"
                  sx={{
                    px: 5,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: '50px',
                    borderWidth: '2px',
                    borderColor: colors.white,
                    color: colors.white,
                    fontWeight: 700,
                    '&:hover': {
                      backgroundColor: alpha(colors.white, 0.1),
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  Schedule Consultation
                </Button>
              </Box>
            </motion.div>
          </Box>

          {/* Contact Section - Improved */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: '24px',
              backgroundColor: colors.white,
              border: `1px solid ${alpha(colors.gray, 0.1)}`,
              boxShadow: `0 8px 24px ${alpha(colors.primary, 0.05)}`
            }}
          >
            <Typography variant="h4" gutterBottom sx={{
              color: colors.primary,
              fontWeight: 800,
              mb: 3,
              fontSize: isMobile ? '1.5rem' : '1.8rem'
            }}>
              Business Banking Support - Zimbabwe
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                  <Box sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    backgroundColor: alpha(colors.accent, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    <Phone sx={{ color: colors.accent, fontSize: '1.2rem' }} />
                  </Box>
                  <Typography variant="body1" sx={{ color: colors.textDark, fontWeight: 500 }}>
                    +263 242 708 888 (Business Banking - Zimbabwe)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                  <Box sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    backgroundColor: alpha(colors.accent, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    <Email sx={{ color: colors.accent, fontSize: '1.2rem' }} />
                  </Box>
                  <Typography variant="body1" sx={{ color: colors.textDark, fontWeight: 500 }}>
                    business@diamondbank.co.zw
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    backgroundColor: alpha(colors.accent, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    <LocationOn sx={{ color: colors.accent, fontSize: '1.2rem' }} />
                  </Box>
                  <Typography variant="body1" sx={{ color: colors.textDark, fontWeight: 500 }}>
                    80 Nelson Mandela Avenue, Harare, Zimbabwe
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                  <Box sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    backgroundColor: alpha(colors.accent, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    <AccountBalance sx={{ color: colors.accent, fontSize: '1.2rem' }} />
                  </Box>
                  <Typography variant="body1" sx={{ color: colors.textDark, fontWeight: 500 }}>
                    Business Hours: Mon-Fri 8:30AM - 4:30PM (Zimbabwe Time)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    backgroundColor: alpha(colors.accent, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    <SupportAgent sx={{ color: colors.accent, fontSize: '1.2rem' }} />
                  </Box>
                  <Typography variant="body1" sx={{ color: colors.textDark, fontWeight: 500 }}>
                    24/7 Emergency Support Available
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button
                component={motion.button}
                whileHover={{ x: 5 }}
                variant="outlined"
                href="/locations"
                endIcon={<ArrowForward />}
                sx={{
                  borderRadius: '50px',
                  px: 4,
                  py: 1.25,
                  borderWidth: '2px',
                  borderColor: colors.accent,
                  color: colors.accent,
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  '&:hover': {
                    backgroundColor: alpha(colors.accent, 0.05),
                    borderWidth: '2px'
                  }
                }}
              >
                Find a Business Center in Zimbabwe
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* CSS animations */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </motion.div>
  );
};

export default Business;